// @ts-nocheck
// ArbScan — calculator suite (Arbitrage / Hedge / 3-Way).
// Faithful implementation of the "ArbScan v2" Claude Design, plus:
//  - light/dark theming via CSS variables (see index.html)
//  - count-up hero, smooth zone transitions, radar sweep, empty state
//  - tap-to-copy on every wager
// All betting math comes from the pure, tested engine in ./calc.
import { useState, useEffect, useRef } from "react";
import {
  calcArb,
  calcHedge,
  calcThreeWay,
  americanFieldToDecimal,
  parsePositive,
  formatMoney,
} from "./calc";

// ---------------------------------------------------------------------------
// Design tokens — colors resolve to CSS variables so the theme can flip live.
// ---------------------------------------------------------------------------
const SANS = "'IBM Plex Sans',sans-serif";
const MONO = "'IBM Plex Mono',monospace";
const INK = "var(--ink)";
const MUTE = "var(--mute)";
const FAINT = "var(--faint)";
const HAIR = "var(--hair)";
const LINE = "var(--line)";
const FIELD = "var(--field)";
const LIME = "#22C55E";
const LOGO = "var(--logo)";
const GHOST = "var(--ghost)";
const THEME_KEY = "arbscan_theme";

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------
const kicker = (mb = 8) => ({
  fontFamily: MONO,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "3px",
  color: MUTE,
  marginBottom: mb,
});

const seg = (on) => ({
  flex: 1,
  minWidth: 0,
  textAlign: "center",
  padding: "9px 6px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: MONO,
  fontSize: "clamp(9px,2.6vw,11px)",
  letterSpacing: ".5px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  background: on ? "var(--seg-on-bg)" : "transparent",
  color: on ? "var(--seg-on-fg)" : FAINT,
  fontWeight: on ? 600 : 400,
  transition: "all .15s ease",
});

const segWrap = {
  display: "flex",
  gap: 4,
  background: FIELD,
  border: `1px solid ${LINE}`,
  borderRadius: 10,
  padding: 4,
  transition: "background .25s ease, border-color .25s ease",
};

const tabPill = (on) => ({
  padding: "8px 16px",
  borderRadius: 999,
  cursor: "pointer",
  fontFamily: SANS,
  fontSize: 13,
  fontWeight: on ? 600 : 400,
  background: on ? LIME : "var(--tab-off-bg)",
  color: on ? "#04070d" : FAINT,
  transition: "all .2s ease",
});

const panel = (zone) => {
  const z = zone === "na" ? "flat" : zone;
  return {
    background: `var(--z-${z}-bg)`,
    border: `1px solid var(--z-${z}-bd)`,
    borderRadius: 16,
    padding: "clamp(16px,5vw,24px)",
    transition: "background .3s ease, border-color .3s ease",
  };
};

const zoneStyle = (color) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  fontFamily: MONO,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "2px",
  color,
  transition: "color .25s ease",
});
const dot = (color) => ({ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block", transition: "background .25s ease" });
const hero = (color, size) => ({
  fontFamily: MONO,
  fontWeight: 500,
  fontSize: size,
  lineHeight: 1,
  letterSpacing: "-1.5px",
  color,
  transition: "color .25s ease",
});
const roiStyle = (color) => ({ fontFamily: MONO, fontSize: 14, color, marginTop: 10, transition: "color .25s ease" });
const cell = (on, soft) => ({
  background: FIELD,
  border: `1px solid ${on ? soft : LINE}`,
  borderRadius: 12,
  padding: 12,
  transition: "border-color .2s ease, background .25s ease",
});

const inputStyle = (fontSize, padding) => ({
  width: "100%",
  background: FIELD,
  border: `1px solid ${LINE}`,
  borderRadius: 12,
  padding,
  fontFamily: MONO,
  fontSize,
  color: INK,
  transition: "background .25s ease, border-color .2s ease, color .25s ease",
});

const decUnder = { fontFamily: MONO, fontSize: 11, color: MUTE, marginTop: 7 };
const cardStyle = { border: `1px solid ${LINE}`, borderRadius: 16, padding: "clamp(16px,5vw,22px)", marginBottom: 16, transition: "border-color .25s ease" };

function pctSigned(n) {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  return (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
}
function decStr(d) {
  if (d === null || d === undefined || !isFinite(d)) return "—";
  return "= " + d.toFixed(3) + " dec";
}
// Clipboard text for a wager: whole number when round, else two decimals.
function copyText(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// Bipolar edge meter derived from an ROI %. Colors are CSS vars (theme-aware).
function meter(roi, kind) {
  const POS = "var(--pos)", AMBER = "var(--amber)", RED = "var(--red)", GREY = "var(--grey)";
  const SOFT = { pos: "var(--pos-soft)", amber: "var(--amber-soft)", red: "var(--red-soft)", grey: "var(--grey-soft)" };
  if (roi === null || roi === undefined || !isFinite(roi)) {
    return { color: GREY, soft: SOFT.grey, zone: "na", label: "—", fill: { display: "none" }, marker: { display: "none" }, middle: false };
  }
  const c = Math.max(-5, Math.min(5, roi));
  const pos = 50 + (c / 5) * 50;
  let color, soft, zone, label;
  if (roi > 0.02) {
    color = POS; soft = SOFT.pos; zone = "good";
    label = roi >= 1.5 ? (kind === "hedge" ? "PROFIT LOCKED" : "STRONG EDGE") : kind === "hedge" ? "PROFIT LOCKED" : "EDGE";
  } else if (Math.abs(roi) <= 0.02) {
    color = GREY; soft = SOFT.grey; zone = "flat"; label = "BREAK EVEN";
  } else if (roi > -3) {
    color = AMBER; soft = SOFT.amber; zone = "mid"; label = kind === "hedge" ? "LOSS LIMITED" : "MIDDLE ZONE";
  } else {
    color = RED; soft = SOFT.red; zone = "bad"; label = kind === "hedge" ? "POOR HEDGE" : "NO PLAY";
  }
  const fillLeft = roi >= 0 ? "50%" : pos + "%";
  const fillWidth = Math.abs(pos - 50) + "%";
  return {
    color, soft, zone, label,
    fill: { position: "absolute", top: 0, height: "100%", left: fillLeft, width: fillWidth, background: color, borderRadius: 999, transition: "left .3s ease, width .3s ease, background .25s ease" },
    marker: { position: "absolute", top: 0, left: pos + "%", width: 2, height: 16, background: color, borderRadius: 2, transform: "translateX(-1px)", transition: "left .3s ease, background .25s ease" },
    middle: zone === "mid",
  };
}

const arbHeroLabel = (zone) =>
  zone === "good" ? "Guaranteed Profit" : zone === "mid" ? "Cost If No Middle" : zone === "flat" ? "Break-Even" : "Worst-Case Loss";

// ---------------------------------------------------------------------------
// Hooks + animated primitives
// ---------------------------------------------------------------------------
function useReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const h = (e) => setReduce(e.matches);
    mq.addEventListener ? mq.addEventListener("change", h) : mq.addListener(h);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", h) : mq.removeListener(h));
  }, []);
  return reduce;
}

// A money figure that eases from its previous value to the new one.
function AnimatedMoney({ value, style }) {
  const reduce = useReducedMotion();
  const [disp, setDisp] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(0);

  useEffect(() => {
    if (value === null || value === undefined || !isFinite(value) || reduce) {
      setDisp(value);
      fromRef.current = value;
      return;
    }
    const from = fromRef.current === null || fromRef.current === undefined || !isFinite(fromRef.current) ? 0 : fromRef.current;
    const to = value;
    if (from === to) {
      setDisp(to);
      return;
    }
    const start = performance.now();
    const dur = 420;
    cancelAnimationFrame(rafRef.current);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - t, 3);
      setDisp(from + (to - from) * e);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, reduce]);

  return <div style={style}>{formatMoney(disp)}</div>;
}

// ---------------------------------------------------------------------------
// Tap-to-copy primitives
// ---------------------------------------------------------------------------
function useCopy() {
  const [copied, setCopied] = useState(false);
  const tRef = useRef(0);
  const copy = (amount) => {
    if (amount === null || amount === undefined || !isFinite(amount)) return;
    try {
      navigator.clipboard && navigator.clipboard.writeText(copyText(amount));
    } catch (e) {}
    setCopied(true);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setCopied(false), 1100);
  };
  useEffect(() => () => clearTimeout(tRef.current), []);
  return [copied, copy];
}

// A wager cell: shows the stake big, optional tag + return, tap to copy.
function StakeCell({ label, tag, amount, payout, highlight, soft, accent, size = 22 }) {
  const [copied, copy] = useCopy();
  const canCopy = amount !== null && amount !== undefined && isFinite(amount);
  const topRight = copied ? "COPIED ✓" : tag || "";
  const topRightColor = copied ? accent : tag === "YOU SET" ? MUTE : accent;
  return (
    <div
      onClick={() => copy(amount)}
      title={canCopy ? "Tap to copy" : undefined}
      style={{ ...cell(highlight, soft), cursor: canCopy ? "pointer" : "default", position: "relative", userSelect: "none" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, minHeight: 11 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "2px", color: MUTE }}>{label}</span>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "1px", color: topRightColor, transition: "color .2s ease" }}>{topRight}</span>
      </div>
      <div style={{ fontFamily: MONO, fontWeight: 500, fontSize: size, lineHeight: 1, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatMoney(amount)}</div>
      {payout !== undefined && <div style={{ fontFamily: MONO, fontSize: 11, color: MUTE, marginTop: 7 }}>returns {formatMoney(payout)}</div>}
    </div>
  );
}

// Inline copyable text (used for the hedge stake in the result header).
function CopyText({ amount, prefix, accent }) {
  const [copied, copy] = useCopy();
  const canCopy = amount !== null && amount !== undefined && isFinite(amount);
  return (
    <span
      onClick={() => copy(amount)}
      title={canCopy ? "Tap to copy" : undefined}
      style={{ cursor: canCopy ? "pointer" : "default", color: copied ? accent : "inherit", transition: "color .2s ease", userSelect: "none" }}
    >
      {copied ? "COPIED ✓" : prefix + formatMoney(amount)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Small shared components
// ---------------------------------------------------------------------------
function Kicker({ children, mb }) {
  return <div style={kicker(mb)}>{children}</div>;
}

function EdgeMeter({ m, leftLabel, rightLabel }) {
  const lbl = { fontFamily: MONO, fontSize: 9, letterSpacing: "2px", color: MUTE };
  const scale = { fontFamily: MONO, fontSize: 9, letterSpacing: "1px", color: GHOST };
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 9 }}>
        <span style={lbl}>{leftLabel}</span>
        <span style={lbl}>{rightLabel}</span>
      </div>
      <div style={{ position: "relative", height: 16 }}>
        <div style={{ position: "absolute", top: 4, left: 0, right: 0, height: 8, background: "var(--meter-track)", borderRadius: 999, overflow: "hidden", transition: "background .25s ease" }}>
          <div style={m.fill}></div>
        </div>
        <div style={{ position: "absolute", top: 1, left: "50%", width: 1, height: 14, background: GHOST, transform: "translateX(-1px)" }}></div>
        <div style={m.marker}></div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        <span style={scale}>-5%</span>
        <span style={scale}>0%</span>
        <span style={scale}>+5%</span>
      </div>
    </>
  );
}

function MiddleNote({ children }) {
  return (
    <div
      style={{
        marginTop: 14,
        background: "var(--note-bg)",
        border: "1px solid var(--note-bd)",
        borderRadius: 10,
        padding: "11px 13px",
        fontFamily: SANS,
        fontSize: 12,
        color: "var(--note-fg)",
        lineHeight: 1.4,
      }}
    >
      {children}
    </div>
  );
}

function EmptyPrompt({ children }) {
  return <div style={{ textAlign: "center", padding: "26px 10px 18px", fontFamily: SANS, fontSize: 14, color: MUTE, lineHeight: 1.5 }}>{children}</div>;
}

function ResultHead({ m, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 10 }}>
      <div style={zoneStyle(m.color)}>
        <span style={dot(m.color)}></span>
        {m.label}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 10, color: MUTE, whiteSpace: "nowrap" }}>{right}</div>
    </div>
  );
}

function PageHead({ kick, title }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <Kicker>{kick}</Kicker>
      <h1 style={{ margin: 0, fontFamily: SANS, fontWeight: 600, fontSize: 24, letterSpacing: "-.3px", color: INK, transition: "color .25s ease" }}>{title}</h1>
    </div>
  );
}

function ThemeToggle({ theme, onToggle }) {
  const dark = theme === "dark";
  return (
    <button
      onClick={onToggle}
      aria-label="Toggle light or dark theme"
      title="Toggle light / dark"
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, border: `1px solid ${LINE}`, background: "transparent", cursor: "pointer", padding: 0, color: MUTE, transition: "border-color .25s ease, color .25s ease" }}
    >
      {dark ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <circle cx="12" cy="12" r="4" />
          <line x1="12" y1="2" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="22" />
          <line x1="2" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="22" y2="12" />
          <line x1="4.9" y1="4.9" x2="7" y2="7" /><line x1="17" y1="17" x2="19.1" y2="19.1" />
          <line x1="4.9" y1="19.1" x2="7" y2="17" /><line x1="17" y1="7" x2="19.1" y2="4.9" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
          <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />
        </svg>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
const DEFAULTS = {
  tab: "arb",
  a1: "-110",
  a2: "+115",
  aMode: "total",
  aStake: "100",
  aRound: true,
  hOdds: "+220",
  hStake: "100",
  hHedge: "-180",
  w1: "+260",
  w2: "+340",
  w3: "+300",
  wMode: "total",
  wLeg: "1",
  wStake: "100",
};

// ---------------------------------------------------------------------------
// Routing — each calculator gets its own URL (History API, no dependencies).
//   /  ,  /arbcalc , /arb   -> Arbitrage
//   /hedge                  -> Hedge
//   /3way , /3-way          -> 3-Way
// vercel.json rewrites every path to index.html, so deep links resolve here.
// ---------------------------------------------------------------------------
const TAB_PATHS = { arb: "/", hedge: "/hedge", way: "/3way" };
const TAB_TITLES = {
  arb: "ArbScan — Arbitrage, Hedge & 3-Way Betting Calculators",
  hedge: "Hedge Calculator — ArbScan",
  way: "3-Way Arbitrage Calculator — ArbScan",
};
function tabFromPath(pathname) {
  const p = (pathname || "/").replace(/\/+$/, "").toLowerCase() || "/";
  if (p === "/hedge") return "hedge";
  if (p === "/3way" || p === "/3-way" || p === "/threeway") return "way";
  return "arb"; // "/", "/arbcalc", "/arb", or anything else
}

export default function App() {
  const [s, setS] = useState(() => ({ ...DEFAULTS, tab: tabFromPath(typeof window !== "undefined" ? window.location.pathname : "/") }));
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light";
    } catch (e) {
      return "light";
    }
  });
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const field = (name) => (e) => set({ [name]: e.target.value });

  // Navigate to a tab and reflect it in the URL.
  const navTo = (tab) => {
    setS((p) => ({ ...p, tab }));
    const path = TAB_PATHS[tab] || "/";
    if (window.location.pathname !== path) {
      window.history.pushState({ tab }, "", path);
    }
  };

  // Keep the tab in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => setS((p) => ({ ...p, tab: tabFromPath(window.location.pathname) }));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Per-route document title for clearer sharing / history.
  useEffect(() => {
    document.title = TAB_TITLES[s.tab] || "ArbScan";
  }, [s.tab]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch (e) {}
  }, [theme]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: INK, overflowX: "hidden", transition: "background .25s ease, color .25s ease" }}>
      {/* NAV */}
      <div style={{ position: "sticky", top: 0, zIndex: 50, height: 56, background: "var(--bg)", borderBottom: `1px solid ${HAIR}`, display: "flex", alignItems: "center", transition: "background .25s ease, border-color .25s ease" }}>
        <div style={{ maxWidth: 600, width: "100%", margin: "0 auto", padding: "0 clamp(14px,5vw,20px)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={LOGO} strokeWidth="1.4">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="6.4"></circle>
              <circle cx="12" cy="12" r="2.9"></circle>
              <circle cx="12" cy="12" r="1.1" fill={LOGO} stroke="none"></circle>
              <g className="radar-sweep">
                <line x1="12" y1="12" x2="20.2" y2="3.8"></line>
              </g>
            </svg>
            <span style={{ fontFamily: SANS, fontWeight: 700, fontSize: 17, letterSpacing: ".5px" }}>
              <span style={{ color: INK }}>ARB</span>
              <span style={{ color: LOGO }}>SCAN</span>
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ThemeToggle theme={theme} onToggle={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} />
            <span style={{ fontFamily: MONO, fontSize: 11, color: MUTE, letterSpacing: ".5px" }}>arbscanning.com</span>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div style={{ background: "var(--bg)", borderBottom: `1px solid ${HAIR}`, transition: "background .25s ease, border-color .25s ease" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "12px clamp(14px,5vw,20px)", display: "flex", gap: 8 }}>
          <div onClick={() => navTo("arb")} style={tabPill(s.tab === "arb")}>Arb Calc</div>
          <div onClick={() => navTo("hedge")} style={tabPill(s.tab === "hedge")}>Hedge</div>
          <div onClick={() => navTo("way")} style={tabPill(s.tab === "way")}>3-Way</div>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: "0 auto", padding: "26px clamp(14px,5vw,20px) 12px" }}>
        {s.tab === "arb" && <ArbTab s={s} set={set} field={field} />}
        {s.tab === "hedge" && <HedgeTab s={s} set={set} field={field} />}
        {s.tab === "way" && <WayTab s={s} set={set} field={field} />}
      </div>

      {/* FOOTER */}
      <div style={{ borderTop: `1px solid ${HAIR}`, marginTop: 28, transition: "border-color .25s ease" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "18px clamp(14px,5vw,20px)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "1px", color: GHOST }}>© 2026 ARBSCAN.COM · FOR INFORMATIONAL USE ONLY</div>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "1px", color: GHOST }}>Find the edge. Beat the books.</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Arbitrage
// ---------------------------------------------------------------------------
function ArbTab({ s, set, field }) {
  const r = calcArb({
    oddsA: s.a1,
    oddsB: s.a2,
    mode: s.aMode === "total" ? "total" : s.aMode === "anchor1" ? "anchorA" : "anchorB",
    stakeA: s.aStake,
    stakeB: s.aStake,
    totalStake: s.aStake,
    roundToDollar: s.aRound,
  });
  const m = meter(r.profitPct, "arb");
  const empty = m.zone === "na";
  const market = r.bookTotal !== null ? (r.bookTotal * 100).toFixed(2) + "%" : "—";
  const stakeLabel = s.aMode === "total" ? "Total Stake" : s.aMode === "anchor1" ? "Book 1 Stake" : "Book 2 Stake";
  const anchored = s.aMode === "anchor1" ? 0 : s.aMode === "anchor2" ? 1 : -1;
  const books = [
    { label: "BOOK 1", leg: r.legA },
    { label: "BOOK 2", leg: r.legB },
  ];

  return (
    <div>
      <PageHead kick="Two-leg arbitrage" title="Arbitrage Calculator" />

      <div style={cardStyle}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }}>
          <div>
            <Kicker mb={10}>Book 1</Kicker>
            <input value={s.a1} onChange={field("a1")} placeholder="-110" style={inputStyle(20, "13px 14px")} />
            <div style={decUnder}>{decStr(americanFieldToDecimal(s.a1))}</div>
          </div>
          <div>
            <Kicker mb={10}>Book 2</Kicker>
            <input value={s.a2} onChange={field("a2")} placeholder="+115" style={inputStyle(20, "13px 14px")} />
            <div style={decUnder}>{decStr(americanFieldToDecimal(s.a2))}</div>
          </div>
        </div>

        <div style={{ height: 1, background: HAIR, margin: "20px 0" }}></div>

        <Kicker mb={10}>Mode</Kicker>
        <div style={segWrap}>
          <div onClick={() => set({ aMode: "total" })} style={seg(s.aMode === "total")}>Total Stake</div>
          <div onClick={() => set({ aMode: "anchor1" })} style={seg(s.aMode === "anchor1")}>Anchor Bk 1</div>
          <div onClick={() => set({ aMode: "anchor2" })} style={seg(s.aMode === "anchor2")}>Anchor Bk 2</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 16, alignItems: "end", marginTop: 16 }}>
          <div>
            <Kicker mb={10}>{stakeLabel}</Kicker>
            <input value={s.aStake} onChange={field("aStake")} inputMode="decimal" placeholder="100" style={inputStyle(20, "13px 14px")} />
          </div>
          <div onClick={() => set({ aRound: !s.aRound })} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", paddingBottom: 13 }}>
            <div style={{ width: 36, height: 20, borderRadius: 999, background: s.aRound ? LIME : "var(--toggle-off)", position: "relative", transition: "all .15s ease", flex: "0 0 auto" }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: "var(--knob)", boxShadow: "0 1px 2px rgba(0,0,0,.2)", position: "absolute", top: 3, left: s.aRound ? 19 : 3, transition: "all .15s ease" }}></div>
            </div>
            <span style={{ fontFamily: MONO, fontSize: 11, color: FAINT, whiteSpace: "nowrap" }}>Round $</span>
          </div>
        </div>
      </div>

      {/* RESULT */}
      <div style={panel(m.zone)}>
        <ResultHead m={m} right={`MARKET ${market}`} />

        {empty ? (
          <EmptyPrompt>Enter both odds and a stake to see your edge.</EmptyPrompt>
        ) : (
          <>
            {/* Per-book wager breakdown — calculated leg highlighted, tap to copy. */}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12, marginBottom: 18 }}>
              {books.map((bk, i) => {
                const isAnchor = i === anchored;
                return (
                  <StakeCell
                    key={i}
                    label={bk.label}
                    tag={anchored === -1 ? "TO BET" : isAnchor ? "YOU SET" : "TO BET"}
                    amount={bk.leg.stake}
                    payout={bk.leg.payout}
                    highlight={!isAnchor && bk.leg.stake !== null}
                    soft={m.soft}
                    accent={m.color}
                  />
                );
              })}
            </div>

            <div style={{ textAlign: "center", padding: "2px 0 18px" }}>
              <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "4px", color: MUTE, marginBottom: 10 }}>{arbHeroLabel(m.zone)}</div>
              <AnimatedMoney value={r.profit} style={hero(m.color, "clamp(34px,10vw,50px)")} />
              <div style={roiStyle(m.color)}>{pctSigned(r.profitPct)} ROI · total {formatMoney(r.totalStake)}</div>
            </div>

            <EdgeMeter m={m} leftLabel="MIDDLE" rightLabel="EDGE" />

            {m.middle && (
              <MiddleNote>
                Negative arb — but a small cost. Viable as a <b>middle</b> if both sides can land (e.g. spreads/totals).
              </MiddleNote>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Hedge
// ---------------------------------------------------------------------------
function HedgeTab({ s, set, field }) {
  const r = calcHedge({ originalOdds: s.hOdds, originalStake: s.hStake, hedgeOdds: s.hHedge, roundToDollar: true });
  const origStake = parsePositive(s.hStake);
  const total = r.hedgeStake !== null && origStake !== null ? r.hedgeStake + origStake : null;
  const roi = r.guaranteedProfit !== null && total !== null && total > 0 ? (r.guaranteedProfit / total) * 100 : null;
  const m = meter(roi, "hedge");
  const empty = m.zone === "na";
  const heroLabel = m.zone === "good" ? "Locked Profit" : m.zone === "flat" ? "Break-Even" : "Locked Loss";
  const outVal = (v) => ({ fontFamily: MONO, fontSize: 20, color: v === null ? MUTE : v >= 0 ? INK : "var(--red)", transition: "color .25s ease" });

  return (
    <div>
      <PageHead kick="Lock in profit" title="Hedge Calculator" />

      <div style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <Kicker mb={10}>Original Bet Odds</Kicker>
          <input value={s.hOdds} onChange={field("hOdds")} placeholder="+220" style={inputStyle(20, "13px 14px")} />
          <div style={decUnder}>{decStr(americanFieldToDecimal(s.hOdds))}</div>
        </div>
        <div>
          <Kicker mb={10}>Original Stake</Kicker>
          <input value={s.hStake} onChange={field("hStake")} inputMode="decimal" placeholder="100" style={inputStyle(20, "13px 14px")} />
        </div>
        <div>
          <Kicker mb={10}>Hedge Odds</Kicker>
          <input value={s.hHedge} onChange={field("hHedge")} placeholder="-180" style={inputStyle(20, "13px 14px")} />
          <div style={decUnder}>{decStr(americanFieldToDecimal(s.hHedge))}</div>
        </div>
      </div>

      {/* RESULT */}
      <div style={panel(m.zone)}>
        <ResultHead m={m} right={<CopyText amount={r.hedgeStake} prefix="HEDGE " accent={m.color} />} />

        {empty ? (
          <EmptyPrompt>Enter your bet and the hedge odds to lock it in.</EmptyPrompt>
        ) : (
          <>
            <div style={{ textAlign: "center", padding: "6px 0 18px" }}>
              <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "4px", color: MUTE, marginBottom: 12 }}>{heroLabel}</div>
              <AnimatedMoney value={r.guaranteedProfit} style={hero(m.color, "clamp(40px,12vw,60px)")} />
              <div style={roiStyle(m.color)}>{pctSigned(roi)} ROI</div>
            </div>

            <EdgeMeter m={m} leftLabel="LOSS" rightLabel="PROFIT" />

            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12, marginTop: 18 }}>
              <div style={{ background: FIELD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 14, transition: "border-color .25s ease, background .25s ease" }}>
                <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "2px", color: MUTE, marginBottom: 7 }}>IF ORIGINAL WINS</div>
                <div style={outVal(r.profitIfOriginalWins)}>{formatMoney(r.profitIfOriginalWins)}</div>
              </div>
              <div style={{ background: FIELD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 14, transition: "border-color .25s ease, background .25s ease" }}>
                <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "2px", color: MUTE, marginBottom: 7 }}>IF HEDGE WINS</div>
                <div style={outVal(r.profitIfHedgeWins)}>{formatMoney(r.profitIfHedgeWins)}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — 3-Way
// ---------------------------------------------------------------------------
function WayTab({ s, set, field }) {
  const r = calcThreeWay({
    oddsA: s.w1,
    oddsB: s.w2,
    oddsC: s.w3,
    mode: s.wMode === "total" ? "total" : "anchor",
    totalStake: s.wStake,
    anchorIndex: parseInt(s.wLeg, 10) - 1,
    anchorStake: s.wStake,
    roundToDollar: true,
  });
  const m = meter(r.profitPct, "arb");
  const empty = m.zone === "na";
  const market = r.bookTotal !== null ? (r.bookTotal * 100).toFixed(2) + "%" : "—";
  const stakeLabel = s.wMode === "total" ? "Total Stake" : "Anchor Stake";
  const payouts = r.legs.map((l) => l.payout);
  const minPay = payouts.every((p) => p !== null) ? Math.min(...payouts) : null;
  const isMin = (p) => minPay !== null && p !== null && Math.abs(p - minPay) < 0.005;
  const legNames = ["HOME", "DRAW", "AWAY"];

  return (
    <div>
      <PageHead kick="Three-outcome market" title="3-Way Calculator" />

      <div style={cardStyle}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12 }}>
          {[
            { f: "w1", label: "Home", ph: "+260" },
            { f: "w2", label: "Draw", ph: "+340" },
            { f: "w3", label: "Away", ph: "+300" },
          ].map((c) => (
            <div key={c.f}>
              <Kicker mb={10}>{c.label}</Kicker>
              <input value={s[c.f]} onChange={field(c.f)} placeholder={c.ph} style={inputStyle(18, 12)} />
              <div style={decUnder}>{decStr(americanFieldToDecimal(s[c.f]))}</div>
            </div>
          ))}
        </div>

        <div style={{ height: 1, background: HAIR, margin: "20px 0" }}></div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }}>
          <div>
            <Kicker mb={10}>Mode</Kicker>
            <div style={segWrap}>
              <div onClick={() => set({ wMode: "total" })} style={seg(s.wMode === "total")}>Total</div>
              <div onClick={() => set({ wMode: "anchor" })} style={seg(s.wMode === "anchor")}>Anchor</div>
            </div>
          </div>
          <div>
            <Kicker mb={10}>{stakeLabel}</Kicker>
            <input value={s.wStake} onChange={field("wStake")} inputMode="decimal" placeholder="100" style={inputStyle(18, "12px 14px")} />
          </div>
        </div>

        {s.wMode === "anchor" && (
          <div style={{ marginTop: 16 }}>
            <Kicker mb={10}>Anchor Leg</Kicker>
            <div style={segWrap}>
              <div onClick={() => set({ wLeg: "1" })} style={seg(s.wLeg === "1")}>Home</div>
              <div onClick={() => set({ wLeg: "2" })} style={seg(s.wLeg === "2")}>Draw</div>
              <div onClick={() => set({ wLeg: "3" })} style={seg(s.wLeg === "3")}>Away</div>
            </div>
          </div>
        )}
      </div>

      {/* RESULT */}
      <div style={panel(m.zone)}>
        <ResultHead m={m} right={`MARKET ${market}`} />

        {empty ? (
          <EmptyPrompt>Enter all three odds and a stake to see your edge.</EmptyPrompt>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10, marginBottom: 18 }}>
              {r.legs.map((leg, i) => (
                <StakeCell
                  key={i}
                  label={legNames[i]}
                  tag=""
                  amount={leg.stake}
                  highlight={isMin(leg.payout)}
                  soft={m.soft}
                  accent={m.color}
                  size={17}
                />
              ))}
            </div>

            <div style={{ textAlign: "center", padding: "2px 0 18px" }}>
              <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "4px", color: MUTE, marginBottom: 10 }}>{arbHeroLabel(m.zone)}</div>
              <AnimatedMoney value={r.profit} style={hero(m.color, "clamp(32px,9vw,48px)")} />
              <div style={roiStyle(m.color)}>
                {pctSigned(r.profitPct)} ROI · returns {formatMoney(r.guaranteedPayout)}
              </div>
            </div>

            <EdgeMeter m={m} leftLabel="MIDDLE" rightLabel="EDGE" />

            {m.middle && (
              <MiddleNote>
                Negative arb — small cost. Could pay off as a <b>middle</b> if outcomes can overlap.
              </MiddleNote>
            )}
          </>
        )}
      </div>
    </div>
  );
}
